import React, { useState } from 'react';
import {
  Badge,
  Button,
  Input,
  Textarea,
  FormLabel,
  Checkbox,
  Switch,
  RadioGroup,
  RadioGroupItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Alert,
  AlertTitle,
  AlertDescription,
  ToastAction,
  useToast,
  Spinner,
  LinearSpinner,
  FullPageLoader,
  cn,
} from '../ui';
import { CodeBlock } from './CodeBlock';
import { TextShimmer } from './TextShimmer';
import {
  IconCopy,
  IconCheck,
  IconRefresh,
  IconChevronDown,
  IconCircle,
  IconWorld,
  IconDots,
  IconCornerDownRight,
  IconX,
  IconSearch,
  IconMinus,
  IconAlertCircle,
  IconSun,
  IconMoon,
  IconSettings2,
  IconUser,
  IconHistory,
  IconChevronRight,
  IconFileText,
  IconLoader2,
  IconPalette,
  IconTypography,
  IconBox,
} from '@tabler/icons-react';

const colors = [
  { name: 'Primary', token: 'primary', class: 'bg-primary', textClass: 'text-background' },
  { name: 'Secondary', token: 'secondary', class: 'bg-secondary', textClass: 'text-secondary-foreground' },
  { name: 'Surface', token: 'surface', class: 'bg-surface', textClass: 'text-surface-foreground' },
  { name: 'Surface Hover', token: 'surface-hover', class: 'bg-surface-hover', textClass: 'text-surface-hover-foreground' },
  { name: 'Background', token: 'background', class: 'bg-background', textClass: 'text-foreground' },
  { name: 'Foreground', token: 'foreground', class: 'bg-foreground', textClass: 'text-background' },
  { name: 'Muted', token: 'muted', class: 'bg-muted', textClass: 'text-muted-foreground' },
  { name: 'Accent', token: 'accent', class: 'bg-accent', textClass: 'text-accent-foreground' },
  { name: 'Destructive', token: 'destructive', class: 'bg-destructive', textClass: 'text-destructive-foreground' },
  { name: 'Card', token: 'card', class: 'bg-card', textClass: 'text-card-foreground' },
  { name: 'Popover', token: 'popover', class: 'bg-popover', textClass: 'text-popover-foreground' },
  { name: 'Border', token: 'border', class: 'bg-border', textClass: 'text-foreground' },
  { name: 'Border Subtle', token: 'border-subtle', class: 'bg-border-subtle', textClass: 'text-foreground' },
  { name: 'Border Strong', token: 'border-strong', class: 'bg-border-strong', textClass: 'text-foreground' },
  { name: 'Input', token: 'input', class: 'bg-input', textClass: 'text-foreground' },
  { name: 'Ring', token: 'ring', class: 'bg-ring', textClass: 'text-background' },
];

const fontSizes = [
  { name: 'XS', token: 'text-xs', size: '0.725rem', lineHeight: '1.2rem' },
  { name: 'SM', token: 'text-sm', size: '0.775rem', lineHeight: '1.3rem' },
  { name: 'Base', token: 'text-base', size: '0.875rem', lineHeight: '1.5rem' },
  { name: 'LG', token: 'text-lg', size: '0.975rem', lineHeight: '1.75rem' },
  { name: 'XL', token: 'text-xl', size: '1.175rem', lineHeight: '1.95rem' },
  { name: '2XL', token: 'text-2xl', size: '1.275rem', lineHeight: '2.25rem' },
  { name: '3XL', token: 'text-3xl', size: '1.375rem', lineHeight: '2.5rem' },
  { name: '4XL', token: 'text-4xl', size: '1.475rem', lineHeight: '2.75rem' },
  { name: '5XL', token: 'text-5xl', size: '3.052rem', lineHeight: 'auto' },
];

const fontWeights = [
  { name: 'Normal', token: 'font-normal', weight: '350' },
  { name: 'Medium', token: 'font-medium', weight: '400' },
  { name: 'Semibold', token: 'font-semibold', weight: '450' },
  { name: 'Bold', token: 'font-bold', weight: '500' },
  { name: 'Black', token: 'font-black', weight: '600' },
];

const animations = [
  { name: 'Accordion Expand', token: 'animate-accordion-expand', description: 'Expands accordion content' },
  { name: 'Accordion Collapse', token: 'animate-accordion-collapse', description: 'Collapses accordion content' },
  { name: 'Fade In Up', token: 'animate-fade-in-up', description: 'Fades in with upward movement' },
  { name: 'Pop In', token: 'animate-pop-in', description: 'Pops in with scale and fade' },
];

const shadows = [
  { name: 'Subtle XS', token: 'shadow-subtle-xs', description: 'Extra small subtle shadow' },
  { name: 'Subtle SM', token: 'shadow-subtle-sm', description: 'Small subtle shadow' },
];

const borderRadius = [
  { name: 'SM', token: 'rounded-sm', value: 'calc(var(--radius) - 4px)' },
  { name: 'MD', token: 'rounded-md', value: 'calc(var(--radius) - 2px)' },
  { name: 'LG', token: 'rounded-lg', value: 'var(--radius)' },
];

export default function DesignSystemPage() {
  const [copied, setCopied] = useState(null);
  const [animationKeys, setAnimationKeys] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('personalization');
  const [themeMode, setThemeMode] = useState('light');
  const [viewMode, setViewMode] = useState('grid');
  const { toast } = useToast();

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const replayAnimation = (animationToken) => {
    setAnimationKeys((prev) => ({ ...prev, [animationToken]: (prev[animationToken] || 0) + 1 }));
  };

  const CopyButton = ({ text, id }) => (
    <button
      type="button"
      onClick={() => copyToClipboard(text, id)}
      className="text-muted-foreground hover:text-foreground flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
    >
      {copied === id ? (
        <>
          <IconCheck size={14} strokeWidth={2} />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <IconCopy size={14} strokeWidth={2} />
          <span>Copy</span>
        </>
      )}
    </button>
  );

  return (
    <div className="bg-background min-h-screen ml-auto mr-auto">
      <div className="mx-auto max-w-6xl px-4 py-12">
        <div className="mb-12">
          <h1 className="text-foreground mb-2 text-4xl font-bold">Design System</h1>
          <p className="text-muted-foreground text-lg">
            Complete reference for all design tokens, colors, typography, and components.
          </p>
        </div>

        {/* Typography Section */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Typography</h2>
          <div className="mb-8">
            <h3 className="text-foreground mb-4 text-xl font-semibold">Font Family</h3>
            <div className="border-border bg-card rounded-lg border p-6">
              <p className="text-foreground mb-2 font-sans text-lg">Space Grotesk</p>
              <code className="text-muted-foreground text-sm">font-sans</code>
            </div>
          </div>
          <div className="mb-8">
            <h3 className="text-foreground mb-4 text-xl font-semibold">Font Sizes</h3>
            <div className="border-border bg-card rounded-lg border p-6">
              <div className="space-y-4">
                {fontSizes.map((size) => (
                  <div key={size.token} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="text-foreground font-medium">{size.name}</span>
                        <code className="text-muted-foreground text-xs">{size.token}</code>
                        <CopyButton text={size.token} id={`size-${size.token}`} />
                      </div>
                      <p className={`text-foreground ${size.token}`}>
                        The quick brown fox jumps over the lazy dog
                      </p>
                    </div>
                    <div className="text-muted-foreground ml-4 text-right text-xs">
                      <div>{size.size}</div>
                      <div>{size.lineHeight}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-foreground mb-4 text-xl font-semibold">Font Weights</h3>
            <div className="border-border bg-card rounded-lg border p-6">
              <div className="space-y-3">
                {fontWeights.map((weight) => (
                  <div key={weight.token} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-foreground font-medium">{weight.name}</span>
                      <code className="text-muted-foreground text-xs">{weight.token}</code>
                      <CopyButton text={weight.token} id={`weight-${weight.token}`} />
                    </div>
                    <p className={`text-foreground ${weight.token} text-lg`}>The quick brown fox</p>
                    <span className="text-muted-foreground ml-4 text-xs">{weight.weight}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Text Gradients Section */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Text Gradients</h2>
          <div className="mb-4">
            <p className="text-muted-foreground text-sm">
              Text gradient effects and animated shimmer. Uses Tailwind gradient utilities and the TextShimmer component.
            </p>
          </div>
          <div className="border-border bg-card rounded-lg border p-6">
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Animated Shimmer</h3>
              <div className="bg-muted/30 border-border rounded-lg border p-6">
                <div className="space-y-4">
                  <TextShimmer duration={1.5} spread={2}>Loading content...</TextShimmer>
                  <TextShimmer duration={2} spread={1.5}>Processing your request</TextShimmer>
                  <TextShimmer duration={1} spread={3} className="text-2xl font-bold">Welcome</TextShimmer>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-foreground mb-4 text-xl font-semibold">Static Gradient Text</h3>
              <div className="space-y-4">
                <p className="from-muted-foreground/50 via-muted-foreground/40 to-muted-foreground/20 bg-gradient-to-r bg-clip-text text-lg font-semibold text-transparent">
                  Gradient from muted colors
                </p>
                <p className="from-primary via-primary/80 to-primary/60 bg-gradient-to-r bg-clip-text text-lg font-semibold text-transparent">
                  Gradient with primary colors
                </p>
                <p className="from-primary via-accent to-primary bg-gradient-to-r bg-clip-text text-xl font-bold text-transparent">
                  Bold gradient text
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Colors Section */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Colors</h2>
          <div className="mb-4">
            <p className="text-muted-foreground text-sm">
              Colors adapt to light and dark modes. Toggle your theme to see the differences.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {colors.map((color) => (
              <div key={color.token} className="border-border bg-card rounded-lg border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h3 className="text-foreground font-semibold">{color.name}</h3>
                    <code className="text-muted-foreground text-xs">{color.token}</code>
                  </div>
                  <CopyButton text={`bg-${color.token}`} id={`color-${color.token}`} />
                </div>
                <div className={`${color.class} ${color.textClass} mb-2 flex h-20 items-center justify-center rounded-md border`}>
                  <span className="text-sm font-medium">Sample</span>
                </div>
                <div className="text-muted-foreground text-xs">
                  <div>
                    Class: <code className="bg-muted rounded px-1">bg-{color.token}</code>
                  </div>
                  {color.textClass && (
                    <div className="mt-1">
                      Text: <code className="bg-muted rounded px-1">{color.textClass}</code>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Dark Mode Examples */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Dark Mode Examples</h2>
          <div className="border-border bg-card rounded-lg border p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="mb-2 text-sm font-medium">Light Mode</div>
                <div className="bg-background border-border rounded-lg border p-4">
                  <div className="mb-2 flex gap-2">
                    <div className="bg-primary text-background rounded px-2 py-1 text-xs">Primary</div>
                    <div className="bg-secondary text-secondary-foreground rounded px-2 py-1 text-xs">Secondary</div>
                    <div className="bg-surface text-surface-foreground rounded px-2 py-1 text-xs">Surface</div>
                  </div>
                  <div className="text-foreground text-sm">This is how colors appear in light mode.</div>
                </div>
              </div>
              <div>
                <div className="mb-2 text-sm font-medium">Dark Mode</div>
                <div className="dark bg-background border-border rounded-lg border p-4">
                  <div className="mb-2 flex gap-2">
                    <div className="bg-primary text-background rounded px-2 py-1 text-xs">Primary</div>
                    <div className="bg-secondary text-secondary-foreground rounded px-2 py-1 text-xs">Secondary</div>
                    <div className="bg-surface text-surface-foreground rounded px-2 py-1 text-xs">Surface</div>
                  </div>
                  <div className="text-foreground text-sm">This is how colors appear in dark mode.</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Animations */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Animations</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {animations.map((animation) => (
              <div key={animation.token} className="border-border bg-card rounded-lg border p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-foreground font-semibold">{animation.name}</h3>
                    <p className="text-muted-foreground text-sm">{animation.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => replayAnimation(animation.token)}
                      className="text-muted-foreground hover:text-foreground flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
                      title="Replay animation"
                    >
                      <IconRefresh size={14} strokeWidth={2} />
                      <span>Replay</span>
                    </button>
                    <CopyButton text={animation.token} id={`anim-${animation.token}`} />
                  </div>
                </div>
                <code className="text-muted-foreground text-xs">{animation.token}</code>
                <div className="mt-4">
                  <div
                    key={animationKeys[animation.token] || 0}
                    className={`bg-primary text-background ${animation.token} flex h-20 items-center justify-center rounded-md`}
                  >
                    <span className="text-sm">Animation Preview</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Shadows */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Shadows</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {shadows.map((shadow) => (
              <div key={shadow.token} className="border-border bg-card rounded-lg border p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-foreground font-semibold">{shadow.name}</h3>
                    <p className="text-muted-foreground text-sm">{shadow.description}</p>
                  </div>
                  <CopyButton text={shadow.token} id={`shadow-${shadow.token}`} />
                </div>
                <code className="text-muted-foreground text-xs">{shadow.token}</code>
                <div className="mt-4">
                  <div className={`bg-background border-border ${shadow.token} flex h-20 items-center justify-center rounded-md border`}>
                    <span className="text-sm">Preview</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Border Radius */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Border Radius</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {borderRadius.map((radius) => (
              <div key={radius.token} className="border-border bg-card rounded-lg border p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-foreground font-semibold">{radius.name}</h3>
                    <p className="text-muted-foreground text-xs">{radius.value}</p>
                  </div>
                  <CopyButton text={radius.token} id={`radius-${radius.token}`} />
                </div>
                <code className="text-muted-foreground text-xs">{radius.token}</code>
                <div className="mt-4">
                  <div className={`bg-primary text-background ${radius.token} flex h-20 items-center justify-center`}>
                    <span className="text-sm">Preview</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Forms Section */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Forms</h2>
          <div className="border-border bg-card rounded-lg border p-6">
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Input Fields</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <FormLabel label="Email Address" />
                  <Input type="email" placeholder="Enter your email" className="mt-2" />
                </div>
                <div>
                  <FormLabel label="Password" />
                  <Input type="password" placeholder="Enter your password" className="mt-2" />
                </div>
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Textarea</h3>
              <FormLabel label="Message" />
              <Textarea placeholder="Enter your message here..." className="mt-2" />
            </div>
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Checkbox</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox id="check1" />
                  <label htmlFor="check1" className="text-foreground cursor-pointer text-sm">Accept terms</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="check2" defaultChecked />
                  <label htmlFor="check2" className="text-foreground cursor-pointer text-sm">Subscribe</label>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Switch</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-foreground text-sm">Enable notifications</label>
                  <Switch />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-foreground text-sm">Dark mode</label>
                  <Switch defaultChecked />
                </div>
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Radio Group</h3>
              <FormLabel label="Select an option" />
              <RadioGroup defaultValue="option1" className="mt-2">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="option1" id="radio1" />
                  <label htmlFor="radio1" className="text-foreground cursor-pointer text-sm">Option 1</label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="option2" id="radio2" />
                  <label htmlFor="radio2" className="text-foreground cursor-pointer text-sm">Option 2</label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="option3" id="radio3" />
                  <label htmlFor="radio3" className="text-foreground cursor-pointer text-sm">Option 3</label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <h3 className="text-foreground mb-4 text-xl font-semibold">Switcher (Theme / View)</h3>
              <div className="space-y-4">
                <div>
                  <FormLabel label="Theme Selection" />
                  <div className="border-border mt-2 flex flex-row gap-2 rounded-lg border p-1">
                    <Button
                      variant={themeMode === 'light' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="flex-1"
                      onClick={() => setThemeMode('light')}
                    >
                      <IconSun size={16} strokeWidth={2} />
                      <span className="ml-2">Light</span>
                    </Button>
                    <Button
                      variant={themeMode === 'dark' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="flex-1"
                      onClick={() => setThemeMode('dark')}
                    >
                      <IconMoon size={16} strokeWidth={2} />
                      <span className="ml-2">Dark</span>
                    </Button>
                  </div>
                </div>
                <div>
                  <FormLabel label="View Mode" />
                  <div className="border-border mt-2 flex flex-row gap-2 rounded-lg border p-1">
                    <Button
                      variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="flex-1"
                      onClick={() => setViewMode('grid')}
                    >
                      Grid
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="flex-1"
                      onClick={() => setViewMode('list')}
                    >
                      List
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Form Label</h3>
              <div className="space-y-4">
                <FormLabel label="Required Field" />
                <FormLabel label="Optional Field" isOptional />
                <FormLabel label="Field with Description" link="https://example.com" linkText="Learn more">
                  This is a helpful description for the field.
                </FormLabel>
              </div>
            </div>
          </div>
        </section>

        {/* Icons (Tabler) */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Icons</h2>
          <p className="text-muted-foreground mb-4 text-sm">
            Icon library: <code className="bg-muted rounded px-1">@tabler/icons-react</code>.
          </p>
          <div className="border-border bg-card rounded-lg border p-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
              {[
                { Icon: IconChevronDown, label: 'IconChevronDown' },
                { Icon: IconCircle, label: 'IconCircle' },
                { Icon: IconWorld, label: 'IconWorld' },
                { Icon: IconDots, label: 'IconDots' },
                { Icon: IconCornerDownRight, label: 'IconCornerDownRight' },
                { Icon: IconX, label: 'IconX' },
                { Icon: IconSearch, label: 'IconSearch' },
                { Icon: IconMinus, label: 'IconMinus' },
                { Icon: IconAlertCircle, label: 'IconAlertCircle' },
                { Icon: IconCheck, label: 'IconCheck' },
                { Icon: IconCopy, label: 'IconCopy' },
                { Icon: IconSun, label: 'IconSun' },
                { Icon: IconMoon, label: 'IconMoon' },
                { Icon: IconSettings2, label: 'IconSettings2' },
                { Icon: IconUser, label: 'IconUser' },
                { Icon: IconHistory, label: 'IconHistory' },
                { Icon: IconChevronRight, label: 'IconChevronRight' },
                { Icon: IconFileText, label: 'IconFileText' },
                { Icon: IconLoader2, label: 'IconLoader2' },
                { Icon: IconPalette, label: 'IconPalette' },
                { Icon: IconTypography, label: 'IconTypography' },
                { Icon: IconBox, label: 'IconBox' },
              ].map(({ Icon, label }) => (
                <div
                  key={label}
                  className="border-border bg-background flex flex-col items-center gap-2 rounded-lg border p-3"
                >
                  <Icon size={24} strokeWidth={2} className="text-foreground" />
                  <span className="text-muted-foreground text-xs">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Loaders */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Loaders</h2>
          <p className="text-muted-foreground mb-4 text-sm">
            Spinner (rotating icon), LinearSpinner (bouncing dots), and FullPageLoader for loading states.
          </p>
          <div className="border-border bg-card rounded-lg border p-6">
            <div className="mb-6">
              <h3 className="text-foreground mb-3 text-lg font-medium">Spinner</h3>
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex flex-col items-center gap-2">
                  <Spinner size="xs" className="text-primary" />
                  <span className="text-muted-foreground text-xs">xs</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <Spinner size="sm" className="text-primary" />
                  <span className="text-muted-foreground text-xs">sm</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <Spinner size="md" className="text-primary" />
                  <span className="text-muted-foreground text-xs">md</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <Spinner size="lg" className="text-primary" />
                  <span className="text-muted-foreground text-xs">lg</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <Spinner size="xl" className="text-primary" />
                  <span className="text-muted-foreground text-xs">xl</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <Spinner size="md" className="text-foreground" />
                  <span className="text-muted-foreground text-xs">foreground</span>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-foreground mb-3 text-lg font-medium">LinearSpinner</h3>
              <LinearSpinner />
            </div>
            <div>
              <h3 className="text-foreground mb-3 text-lg font-medium">FullPageLoader</h3>
              <div className="border-border flex min-h-[200px] items-center justify-center rounded-lg border">
                <FullPageLoader label="Loading..." />
              </div>
            </div>
          </div>
        </section>

        {/* Table Styles */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Table Styles</h2>
          <div className="border-border bg-card rounded-lg border p-6">
            <div className="mb-4 overflow-hidden rounded-lg border border-border">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-border border-b">
                    <th className="bg-surface text-muted-foreground px-3 py-1.5 text-left text-sm font-medium">Name</th>
                    <th className="bg-surface text-muted-foreground px-3 py-1.5 text-left text-sm font-medium">Role</th>
                    <th className="bg-surface text-muted-foreground px-3 py-1.5 text-left text-sm font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-border border-b">
                    <td className="px-3 py-2.5 text-foreground">John Doe</td>
                    <td className="px-3 py-2.5 text-foreground">Developer</td>
                    <td className="px-3 py-2.5 text-foreground">
                      <Badge variant="secondary">Active</Badge>
                    </td>
                  </tr>
                  <tr className="border-border border-b">
                    <td className="px-3 py-2.5 text-foreground">Jane Smith</td>
                    <td className="px-3 py-2.5 text-foreground">Designer</td>
                    <td className="px-3 py-2.5 text-foreground">
                      <Badge variant="secondary">Active</Badge>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2.5 text-foreground">Bob Johnson</td>
                    <td className="px-3 py-2.5 text-foreground">Manager</td>
                    <td className="px-3 py-2.5 text-foreground">
                      <Badge variant="outline">Inactive</Badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Code Block */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Code Block</h2>
          <div className="border-border bg-card rounded-lg border p-6">
            <CodeBlock
              lang="javascript"
              code={`function greet(name) {
  return \`Hello, \${name}!\`;
}
const message = greet('World');`}
            />
            <div className="mt-4">
              <CodeBlock showHeader={false} lang="text" code="// Code without header" />
            </div>
          </div>
        </section>

        {/* Modal Templates */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Modal Templates</h2>
          <div className="border-border bg-card rounded-lg border p-6">
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Dialog</h3>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="primary">Open Dialog</Button>
                </DialogTrigger>
                <DialogContent ariaTitle="Example Dialog">
                  <DialogHeader>
                    <DialogTitle>Dialog Title</DialogTitle>
                    <DialogDescription>
                      This is a dialog description. It provides context about the dialog&apos;s purpose.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <p className="text-foreground text-sm">
                      Dialog content goes here. You can add forms, text, or other components.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="primary" onClick={() => setDialogOpen(false)}>Confirm</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <div className="mt-4">
                <CodeBlock
                  lang="jsx"
                  code={`<Dialog open={open} onOpenChange={setOpen}>
  <DialogTrigger asChild><Button>Open</Button></DialogTrigger>
  <DialogContent ariaTitle="Title">
    <DialogHeader>...</DialogHeader>
    <DialogFooter>...</DialogFooter>
  </DialogContent>
</Dialog>`}
                />
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Alert Dialog</h3>
              <AlertDialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">Delete Item</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the item.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setAlertDialogOpen(false)}>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => setAlertDialogOpen(false)}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <div>
              <h3 className="text-foreground mb-4 text-xl font-semibold">Settings Modal</h3>
              <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
                <DialogTrigger asChild>
                  <Button variant="primary">Open Settings</Button>
                </DialogTrigger>
                <DialogContent ariaTitle="Settings" className="max-h-[500px] max-w-[560px] overflow-hidden p-0">
                  <h3 className="border-border border-b px-5 py-4 text-lg font-bold">Settings</h3>
                  <div className="flex gap-6 p-4">
                    <div className="flex w-40 shrink-0 flex-col gap-1">
                      <Button
                        rounded="full"
                        className="justify-start"
                        variant={settingsTab === 'personalization' ? 'secondary' : 'ghost'}
                        onClick={() => setSettingsTab('personalization')}
                      >
                        <IconSettings2 size={16} strokeWidth={2} className="text-muted-foreground" />
                        Customize
                      </Button>
                      <Button
                        rounded="full"
                        className="justify-start"
                        variant={settingsTab === 'api-keys' ? 'secondary' : 'ghost'}
                        onClick={() => setSettingsTab('api-keys')}
                      >
                        API Keys
                      </Button>
                    </div>
                    <div className="flex-1 px-4">
                      {settingsTab === 'personalization' && (
                        <div className="space-y-4">
                          <h3 className="text-base font-semibold">Theme</h3>
                          <p className="text-muted-foreground text-sm">Choose your preferred theme.</p>
                          <div className="border-border flex gap-2 rounded-lg border p-1">
                            <Button variant="secondary" size="sm" className="flex-1">
                              <IconSun size={16} strokeWidth={2} />
                              <span className="ml-2">Light</span>
                            </Button>
                            <Button variant="ghost" size="sm" className="flex-1">
                              <IconMoon size={16} strokeWidth={2} />
                              <span className="ml-2">Dark</span>
                            </Button>
                          </div>
                        </div>
                      )}
                      {settingsTab === 'api-keys' && (
                        <div className="space-y-4">
                          <h3 className="text-base font-semibold">API Keys</h3>
                          <FormLabel label="OpenAI API Key" />
                          <Input type="password" placeholder="sk-..." className="mt-2" />
                        </div>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </section>

        {/* Components */}
        <section className="mb-16">
          <h2 className="text-foreground mb-6 text-2xl font-semibold">Components</h2>
          <div className="border-border bg-card rounded-lg border p-6">
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Buttons</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="default" size="sm">Default</Button>
                <Button variant="primary" size="sm">Primary</Button>
                <Button variant="secondary" size="sm">Secondary</Button>
                <Button variant="outlined" size="sm">Outlined</Button>
                <Button variant="destructive" size="sm">Destructive</Button>
                <Button variant="ghost" size="sm">Ghost</Button>
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Badges</h3>
              <div className="flex flex-wrap gap-3">
                <Badge variant="default">Default</Badge>
                <Badge variant="secondary">Secondary</Badge>
                <Badge variant="primary">Primary</Badge>
                <Badge variant="destructive">Destructive</Badge>
                <Badge variant="outline">Outline</Badge>
              </div>
            </div>
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Dropdown</h3>
              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outlined" size="sm" className="gap-1.5">
                    Open menu
                    <IconChevronDown
                      size={16}
                      strokeWidth={2}
                      className={cn('transition-transform duration-200', dropdownOpen && 'rotate-180')}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    Copy <DropdownMenuShortcut>⌘C</DropdownMenuShortcut>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    Paste <DropdownMenuShortcut>⌘V</DropdownMenuShortcut>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>Settings</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="mb-6">
              <h3 className="text-foreground mb-4 text-xl font-semibold">Alerts</h3>
              <div className="space-y-3">
                <Alert variant="default">
                  <IconAlertCircle size={16} strokeWidth={2} />
                  <AlertTitle>Default Alert</AlertTitle>
                  <AlertDescription>General information message.</AlertDescription>
                </Alert>
                <Alert variant="success">
                  <IconCheck size={16} strokeWidth={2} />
                  <AlertTitle>Success</AlertTitle>
                  <AlertDescription>Your action was completed successfully.</AlertDescription>
                </Alert>
                <Alert variant="destructive">
                  <IconAlertCircle size={16} strokeWidth={2} />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>Something went wrong. Please try again.</AlertDescription>
                </Alert>
              </div>
            </div>
            <div>
              <h3 className="text-foreground mb-4 text-xl font-semibold">Toasts</h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => toast({ title: 'Default Toast', description: 'A default toast notification.', variant: 'default' })}
                >
                  Default Toast
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => toast({ title: 'Error', description: 'Something went wrong.', variant: 'destructive' })}
                >
                  Error Toast
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    toast({
                      title: 'Action Required',
                      description: 'Please complete this action.',
                      variant: 'default',
                      action: (
                        <ToastAction altText="Open Settings">
                          <Button variant="primary" size="sm">Open Settings</Button>
                        </ToastAction>
                      ),
                    })
                  }
                >
                  Toast with Action
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
